import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthUser {
  username: string;
  loginTime: number;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (username: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * 인증 컨텍스트 프로바이더
 * 관리자 인증 상태를 관리합니다.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 로컬 스토리지에서 인증 정보 복원
  useEffect(() => {
    const savedUser = localStorage.getItem('admin-auth');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // 로그인 시간으로부터 24시간 후 자동 로그아웃
        const isExpired = Date.now() - parsedUser.loginTime > 24 * 60 * 60 * 1000;
        
        if (!isExpired) {
          setUser(parsedUser);
        } else {
          localStorage.removeItem('admin-auth');
        }
      } catch (error) {
        console.error('인증 정보 복원 오류:', error);
        localStorage.removeItem('admin-auth');
      }
    }
    setIsLoading(false);
  }, []);

  // 로그인 함수
  const login = async (username: string): Promise<void> => {
    setIsLoading(true);
    
    try {
      // 시뮬레이션 딜레이 (실제 환경에서는 서버 인증 API 호출)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const authUser: AuthUser = {
        username,
        loginTime: Date.now()
      };
      
      setUser(authUser);
      localStorage.setItem('admin-auth', JSON.stringify(authUser));
    } catch (error) {
      console.error('로그인 오류:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 로그아웃 함수
  const logout = (): void => {
    setUser(null);
    localStorage.removeItem('admin-auth');
  };

  // 컨텍스트 값
  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 인증 컨텍스트 사용 훅
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * 인증 여부 확인 훅
 */
export const useRequireAuth = (): AuthUser => {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !user) {
    throw new Error('Authentication required');
  }
  
  return user;
}; 